import { useEffect, useRef, type ReactNode } from "react";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { Autocomplete, Dialog, Header, Input, Menu, MenuItem, MenuSection, Modal, ModalOverlay, SearchField, Text, useFilter, type Key } from "react-aria-components";

export interface CommandItem {
  id: string;
  label: string;
  /** Extra zoektermen (referentie, plaats, Engelse naam). */
  keywords?: string;
  hint?: string;
  icon?: ReactNode;
  group: string;
}

export interface CommandPaletteProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  items: CommandItem[];
  onAction: (id: string) => void;
  label: string;
  placeholder: string;
  inputValue: string;
  onInputChange: (value: string) => void;
  footer?: ReactNode;
  emptyText: string;
}

/** Opdrachtpalet (Ctrl/Cmd+K): navigeren en acties op naam of referentie. */
export function CommandPalette({ isOpen, onOpenChange, items, onAction, label, placeholder, inputValue, onInputChange, footer, emptyText }: CommandPaletteProps) {
  const { contains } = useFilter({ sensitivity: "base" });
  const groups = [...new Set(items.map((i) => i.group))];
  // Treffers die pas na het typen binnenkomen (zoekopdracht naar de server) krijgen alsnog de eerste regel gemarkeerd,
  // zoals react-aria dat bij typen doet; zo kiest Enter wat je ziet. 'react-aria-focus' is hetzelfde event dat
  // Autocomplete zelf naar de lijst stuurt (react-aria-components 1.21).
  const menuRef = useRef<HTMLDivElement>(null);
  const input = useRef(inputValue);
  input.current = inputValue;
  const itemsKey = items.map((i) => i.id).join("|");
  useEffect(() => {
    if (!isOpen || !input.current.trim()) return;
    menuRef.current?.dispatchEvent(new CustomEvent("react-aria-focus", { bubbles: true, cancelable: true, detail: { focusStrategy: "first" } }));
  }, [itemsKey, isOpen]);
  return (
    <ModalOverlay className="ui-modal-overlay" isOpen={isOpen} onOpenChange={onOpenChange} isDismissable>
      <Modal className="ui-modal ui-command">
        <Dialog className="ui-dialog" aria-label={label}>
          {({ close }) => (
            <Autocomplete
              inputValue={inputValue}
              onInputChange={onInputChange}
              filter={(textValue, input) => contains(textValue, input)}
            >
              <SearchField
                aria-label={label}
                autoFocus
                className="ui-command__search"
                onSubmit={(value) => {
                  // Enter zonder gekozen regel: open de eerste treffer.
                  const first = items.find((i) => contains(`${i.label} ${i.keywords ?? ""}`, value));
                  if (first) {
                    onAction(first.id);
                    close();
                  }
                }}
              >
                <MagnifyingGlassIcon aria-hidden />
                <Input placeholder={placeholder} />
              </SearchField>
              <Menu
                ref={menuRef}
                className="ui-menu"
                renderEmptyState={() => <p className="ui-empty" style={{ padding: "var(--space-6)" }}>{emptyText}</p>}
                onAction={(key: Key) => {
                  onAction(String(key));
                  close();
                }}
              >
                {groups.map((group) => (
                  <MenuSection key={group}>
                    <Header className="ui-menu-section-head">{group}</Header>
                    {items
                      .filter((i) => i.group === group)
                      .map((i) => (
                        <MenuItem key={i.id} id={i.id} textValue={`${i.label} ${i.keywords ?? ""}`} className="ui-menu-item">
                          {i.icon}
                          <Text slot="label" className="truncate">
                            {i.label}
                          </Text>
                          {i.hint ? <span className="ui-menu-item__kbd">{i.hint}</span> : null}
                        </MenuItem>
                      ))}
                  </MenuSection>
                ))}
              </Menu>
              {footer ? <div className="ui-command__foot">{footer}</div> : null}
            </Autocomplete>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
